import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useDesignStore from '../../store/designStore';
import useProjectStore from '../../store/projectStore';
import useAuthStore from '../../store/authStore';
import useUIStore from '../../store/uiStore';
import ScrollX from '../../components/ScrollX';

const initialForm = {
  project_id: '',
  construction_type: 'two_bed',
  design_flow_m3hr: '',
  number_of_vessels: '1',
  vessel_construction: 'frp',
  raw_water_total_cations_ppm_as_caco3: '',
  raw_water_total_anions_ppm_as_caco3: '',
  run_length_hours: '24',
  cation_capacity_g_per_liter: '45',
  cation_acid_dose_g_per_liter_resin: '80',
  anion_capacity_g_per_liter: '35',
  anion_caustic_dose_g_per_liter_resin: '100',
  max_service_velocity_m_hr: '40',
  backwash_rate_m_hr: '10',
  backwash_duration_min: '10',
  mb_polish_load_fraction_percent: '2',
};

const DMPlantCalculatorPage = () => {
  const [form, setForm] = useState(initialForm);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showDetails, setShowDetails] = useState(true);
  const { projects, fetchProjects } = useProjectStore();
  const { user } = useAuthStore();
  const { result, isCalculating, isSubmitting, error, calculate, saveCalculation, clearResult } = useDesignStore();
  const showToast = useUIStore((s) => s.showToast);
  const navigate = useNavigate();

  const includesMb = form.construction_type === 'two_bed_plus_mb';

  useEffect(() => {
    fetchProjects();
    clearResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) showToast(error, 'error');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  const buildInputs = () => {
    if (!form.design_flow_m3hr || !form.raw_water_total_cations_ppm_as_caco3 || !form.raw_water_total_anions_ppm_as_caco3) {
      showToast('Design flow, total cations, and total anions are required', 'error');
      return null;
    }
    return {
      construction_type: form.construction_type,
      design_flow_m3hr: Number(form.design_flow_m3hr),
      number_of_vessels: Number(form.number_of_vessels),
      vessel_construction: form.vessel_construction,
      raw_water_total_cations_ppm_as_caco3: Number(form.raw_water_total_cations_ppm_as_caco3),
      raw_water_total_anions_ppm_as_caco3: Number(form.raw_water_total_anions_ppm_as_caco3),
      run_length_hours: Number(form.run_length_hours),
      cation_capacity_g_per_liter: Number(form.cation_capacity_g_per_liter),
      cation_acid_dose_g_per_liter_resin: Number(form.cation_acid_dose_g_per_liter_resin),
      anion_capacity_g_per_liter: Number(form.anion_capacity_g_per_liter),
      anion_caustic_dose_g_per_liter_resin: Number(form.anion_caustic_dose_g_per_liter_resin),
      max_service_velocity_m_hr: Number(form.max_service_velocity_m_hr),
      backwash_rate_m_hr: Number(form.backwash_rate_m_hr),
      backwash_duration_min: Number(form.backwash_duration_min),
      mb_polish_load_fraction_percent: includesMb ? Number(form.mb_polish_load_fraction_percent) : null,
    };
  };

  const handleCalculate = async (e) => {
    e.preventDefault();
    const inputs = buildInputs();
    if (!inputs) return;
    await calculate('dm_plant', inputs);
  };

  const handleSave = async () => {
    if (!result) return;
    const payload = {
      calculator_type: 'dm_plant',
      project_id: form.project_id ? Number(form.project_id) : null,
      created_by_id: user?.id || null,
      client_data: result.inputs,
      calculation_outputs: result.outputs,
    };
    const saveResult = await saveCalculation(payload);
    if (saveResult.success) {
      showToast('Calculation saved as draft', 'success');
      navigate('/design/calculations');
    } else {
      showToast(saveResult.error, 'error');
    }
  };

  const cation = result?.outputs?.cation;
  const anion = result?.outputs?.anion;
  const mb = result?.outputs?.mb;

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-xl font-bold text-white mb-1">DM Plant (Two-Bed + Optional Mixed Bed)</h1>
        <p className="text-xs text-gray-500 mb-6">
          Cation (SAC/HCl) and anion (SBA/NaOH) beds sized like Softener — vessel selected purely
          by resin volume (no bed depth input), velocity is a cross-check only. Optional MB
          polisher — see "Assumptions" below the result.
        </p>

        <div className="glass-card p-6">
          <form onSubmit={handleCalculate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project (optional)</label>
              <select
                value={form.project_id}
                onChange={handleChange('project_id')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">Not linked to a project</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Configuration *</label>
              <div className="flex gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={form.construction_type === 'two_bed'}
                    onChange={() => setForm((f) => ({ ...f, construction_type: 'two_bed' }))}
                  />
                  Two-Bed (Cation + Anion)
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={form.construction_type === 'two_bed_plus_mb'}
                    onChange={() => setForm((f) => ({ ...f, construction_type: 'two_bed_plus_mb' }))}
                  />
                  Two-Bed + Mixed Bed Polisher
                </label>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Design Flow (m³/hr) *</label>
                <input
                  required
                  type="number"
                  step="any"
                  value={form.design_flow_m3hr}
                  onChange={handleChange('design_flow_m3hr')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Cations (ppm as CaCO₃) *</label>
                <input
                  required
                  type="number"
                  step="any"
                  value={form.raw_water_total_cations_ppm_as_caco3}
                  onChange={handleChange('raw_water_total_cations_ppm_as_caco3')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Anions (ppm as CaCO₃) *</label>
                <input
                  required
                  type="number"
                  step="any"
                  value={form.raw_water_total_anions_ppm_as_caco3}
                  onChange={handleChange('raw_water_total_anions_ppm_as_caco3')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Run Length (hr)</label>
                <input
                  type="number"
                  step="any"
                  value={form.run_length_hours}
                  onChange={handleChange('run_length_hours')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  placeholder="Default: 24hr"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1"># of Trains</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={form.number_of_vessels}
                  onChange={handleChange('number_of_vessels')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vessel Construction</label>
                <select
                  value={form.vessel_construction}
                  onChange={handleChange('vessel_construction')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  <option value="frp">FRP (standard sizes)</option>
                  <option value="ms_fabricated">MS (fabricated, round to 50mm)</option>
                </select>
              </div>
            </div>

            {includesMb && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">MB Polish Load Fraction (%)</label>
                <input
                  type="number"
                  step="any"
                  value={form.mb_polish_load_fraction_percent}
                  onChange={handleChange('mb_polish_load_fraction_percent')}
                  className="w-48 border border-gray-300 rounded px-3 py-2 text-sm"
                  placeholder="Default: 2% of primary loads"
                />
              </div>
            )}

            <div>
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                className="text-xs font-semibold text-komal-blue hover:underline"
              >
                {showAdvanced ? '− Hide' : '+ Show'} resin, regenerant &amp; bed parameters
              </button>

              {showAdvanced && (
                <div className="grid grid-cols-3 gap-4 mt-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Cation Capacity (g CaCO₃/L)</label>
                    <input type="number" step="any" value={form.cation_capacity_g_per_liter} onChange={handleChange('cation_capacity_g_per_liter')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">HCl Dose (g/L resin)</label>
                    <input type="number" step="any" value={form.cation_acid_dose_g_per_liter_resin} onChange={handleChange('cation_acid_dose_g_per_liter_resin')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Anion Capacity (g CaCO₃/L)</label>
                    <input type="number" step="any" value={form.anion_capacity_g_per_liter} onChange={handleChange('anion_capacity_g_per_liter')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">NaOH Dose (g/L resin)</label>
                    <input type="number" step="any" value={form.anion_caustic_dose_g_per_liter_resin} onChange={handleChange('anion_caustic_dose_g_per_liter_resin')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Max Service Velocity (m/hr, cross-check)</label>
                    <input type="number" step="any" value={form.max_service_velocity_m_hr} onChange={handleChange('max_service_velocity_m_hr')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Backwash Rate (m/hr)</label>
                    <input type="number" step="any" value={form.backwash_rate_m_hr} onChange={handleChange('backwash_rate_m_hr')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Backwash Duration (min)</label>
                    <input type="number" step="any" value={form.backwash_duration_min} onChange={handleChange('backwash_duration_min')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="submit"
                disabled={isCalculating}
                className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60"
              >
                {isCalculating ? 'Calculating...' : 'Calculate'}
              </button>
            </div>
          </form>
        </div>

        {result && cation && anion && (
          <div className="glass-card p-6 mt-4">
            <h2 className="font-semibold text-gray-900 mb-4">Result</h2>

            {result.outputs.warnings?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4 space-y-1">
                {result.outputs.warnings.map((w, i) => (
                  <p key={i} className="text-xs text-amber-800">⚠ {w}</p>
                ))}
              </div>
            )}

            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Overall</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm mb-4">
              <Stat label="Flow per Train" value={`${result.outputs.flow_per_vessel_m3hr} m³/hr`} />
              <Stat label="Regenerations / Day" value={result.outputs.regenerations_per_day} />
              <Stat label="HCl / Day" value={`${result.outputs.acid_kg_per_day_total} kg`} highlight />
              <Stat label="NaOH / Day" value={`${result.outputs.caustic_kg_per_day_total} kg`} highlight />
            </div>

            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Cation Bed (SAC / HCl)</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm mb-4">
              <Stat label="Resin Volume" value={`${cation.required_resin_volume_liters} L`} highlight />
              <Stat
                label="Diameter (selected)"
                value={`${cation.diameter_recommended_mm} mm (${cation.diameter_recommended_inches})`}
                highlight
              />
              <Stat label="Bed Depth (derived)" value={`${cation.actual_bed_depth_mm} mm`} />
              <Stat label="Actual Velocity (cross-check)" value={`${cation.actual_velocity_m_hr} m/hr`} />
              <Stat
                label={cation.height_recommended_mm != null ? 'Vessel Height (catalog)' : 'Vessel Height (fabricated)'}
                value={
                  cation.height_recommended_inches
                    ? `${cation.overall_height_mm} mm (${cation.height_recommended_inches})`
                    : `${cation.overall_height_mm} mm`
                }
                highlight={cation.vessel_fits}
              />
              {!cation.vessel_fits && <Stat label="Fits Catalog?" value="No — see warning above" highlight />}
              <Stat label="HCl / Regeneration" value={`${cation.acid_kg_per_regeneration_total} kg`} highlight />
            </div>

            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Anion Bed (SBA / NaOH)</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm mb-4">
              <Stat label="Resin Volume" value={`${anion.required_resin_volume_liters} L`} highlight />
              <Stat
                label="Diameter (selected)"
                value={`${anion.diameter_recommended_mm} mm (${anion.diameter_recommended_inches})`}
                highlight
              />
              <Stat label="Bed Depth (derived)" value={`${anion.actual_bed_depth_mm} mm`} />
              <Stat label="Actual Velocity (cross-check)" value={`${anion.actual_velocity_m_hr} m/hr`} />
              <Stat
                label={anion.height_recommended_mm != null ? 'Vessel Height (catalog)' : 'Vessel Height (fabricated)'}
                value={
                  anion.height_recommended_inches
                    ? `${anion.overall_height_mm} mm (${anion.height_recommended_inches})`
                    : `${anion.overall_height_mm} mm`
                }
                highlight={anion.vessel_fits}
              />
              {!anion.vessel_fits && <Stat label="Fits Catalog?" value="No — see warning above" highlight />}
              <Stat label="NaOH / Regeneration" value={`${anion.caustic_kg_per_regeneration_total} kg`} highlight />
            </div>

            {mb && (
              <>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Mixed Bed (MB) Polisher</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm mb-4">
                  <Stat label="Total Resin Volume" value={`${mb.required_total_resin_volume_liters} L`} highlight />
                  <Stat
                    label="Diameter (selected)"
                    value={`${mb.diameter_recommended_mm} mm (${mb.diameter_recommended_inches})`}
                    highlight
                  />
                  <Stat label="Bed Depth (derived)" value={`${mb.actual_bed_depth_mm} mm`} />
                  <Stat
                    label={mb.height_recommended_mm != null ? 'Vessel Height (catalog)' : 'Vessel Height (fabricated)'}
                    value={
                      mb.height_recommended_inches
                        ? `${mb.overall_height_mm} mm (${mb.height_recommended_inches})`
                        : `${mb.overall_height_mm} mm`
                    }
                    highlight={mb.vessel_fits}
                  />
                  {!mb.vessel_fits && <Stat label="Fits Catalog?" value="No — see warning above" highlight />}
                  <Stat label="HCl / Regeneration" value={`${mb.acid_kg_per_regeneration_per_vessel} kg`} />
                  <Stat label="NaOH / Regeneration" value={`${mb.caustic_kg_per_regeneration_per_vessel} kg`} />
                </div>
              </>
            )}

            {(result.outputs.calculation_steps || result.outputs.assumptions) && (
              <div className="mt-6 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowDetails((v) => !v)}
                  className="text-xs font-semibold text-komal-blue hover:underline mb-3"
                >
                  {showDetails ? '− Hide calculation & assumptions' : '+ Show calculation & assumptions'}
                </button>

                {showDetails && (
                  <div className="space-y-4">
                    {result.outputs.calculation_steps?.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                          Calculation Steps
                        </h3>
                        <div className="bg-gray-50 rounded border border-gray-100">
                          <ScrollX>
                          <table className="w-full text-xs">
                            <tbody>
                              {result.outputs.calculation_steps.map((step, i) => (
                                <tr key={i} className="border-b border-gray-100 last:border-0">
                                  <td className="px-3 py-2 font-medium text-gray-700 whitespace-nowrap">{step.step}</td>
                                  <td className="px-3 py-2 text-gray-500 font-mono whitespace-nowrap">{step.formula}</td>
                                  <td className="px-3 py-2 text-gray-900 font-mono">{step.result}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </ScrollX>
                        </div>
                      </div>
                    )}

                    {result.outputs.assumptions?.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                          Assumptions
                        </h3>
                        <ul className="list-disc list-inside space-y-1">
                          {result.outputs.assumptions.map((a, i) => (
                            <li key={i} className="text-xs text-gray-600">{a}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-6">
              <button
                type="button"
                onClick={() => clearResult()}
                className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm rounded bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-60"
              >
                {isSubmitting ? 'Saving...' : 'Save as Draft'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, highlight }) => (
  <div>
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`font-semibold ${highlight ? 'text-komal-blue text-lg' : 'text-gray-900'}`}>{value}</div>
  </div>
);

export default DMPlantCalculatorPage;
